/**
 * BIM ⇄ SceneObject projection (Phase A1).
 *
 * The BIM model (core/types.BIMElement) stays untouched; these functions
 * PROJECT elements into the shared protocol and merge protocol-level edits
 * (visibility, lock, layer) back. Payload (`data`) carries the original
 * element so shared renderers/pickers can resolve geometry without the
 * BIM store, while owning code keeps reading the store's rich element.
 */

import type { Element, BIMElement } from '../types';
import type { SceneKind, SceneObject } from './types';
import { identityPlacement } from './types';

/** BIM-style category string → shared semantic kind. */
const BIM_CATEGORY_TO_KIND: Record<string, SceneKind> = {
    Walls: 'wall',
    Doors: 'door',
    Windows: 'window',
    Floors: 'floor',
    Roofs: 'roof',
    Ceilings: 'ceiling',
    StructuralFraming: 'beam',
    Columns: 'column',
    Trusses: 'truss',
    Braces: 'brace',
    Stairs: 'stair',
    Railings: 'railing',
    Ramps: 'railing',
    'Curtain Walls': 'curtainWall',
    Mass: 'mass',
    Rooms: 'room',
    Areas: 'room',
    Ducts: 'duct',
    FlexDucts: 'duct',
    Pipes: 'pipe',
    FlexPipes: 'pipe',
    PipePlaceholders: 'pipe',
    CableTrays: 'cableTray',
    Conduits: 'conduit',
    'Air Terminals': 'equipment',
    'Mechanical Equipment': 'equipment',
    'Plumbing Fixtures': 'fixture',
    Sprinklers: 'equipment',
    'Fabrication Parts': 'structural',
    Annotations: 'annotation',
    'Detail Items': 'detail',
    'Site': 'structural',
    StructuralColumns: 'column',
    StructuralFoundations: 'structural',
    Levels: 'level',
    Grids: 'grid',
    Groups: 'group',
    Decals: 'decal',
    Links: 'link',
    'Precast Walls': 'wall',
    'Precast Floors': 'floor',
    'Precast Columns': 'column',
    'Precast Beams': 'beam',
    'Precast Mounting Parts': 'structural',
    'Precast Reinforcement': 'structural',
};

export function bimCategoryToKind(category: string): SceneKind {
    return BIM_CATEGORY_TO_KIND[category] ?? 'generic';
}

/**
 * Best-effort placement extraction from heterogeneous geometryData.
 * Elements keep their own transforms (start/end for walls, host-relative
 * offsets for doors, ...); this projects only a sensible ORIGIN so shared
 * code (bounds, gumball, snapping) has a common anchor to work with.
 */
function extractPlacement(element: Element): SceneObject['placement'] {
    const gd = element.geometryData as Record<string, unknown> | undefined;
    if (gd) {
        const pos = gd.position ?? gd.location ?? gd.center;
        if (Array.isArray(pos) && pos.length === 3) {
            const [x, y, z] = pos as [number, number, number];
            return { position: [x, y, z], rotation: [0, 0, 0], scale: [1, 1, 1] };
        }
        // Linear elements (walls/braces/ducts): start point anchors placement.
        const start = gd.start;
        if (Array.isArray(start) && start.length === 3) {
            const [x, y, z] = start as [number, number, number];
            return { position: [x, y, z], rotation: [0, 0, 0], scale: [1, 1, 1] };
        }
    }
    return identityPlacement();
}

/** Layer contract: BIM elements report their category to the resolver. */
function layerOf(element: Element): string {
    return (element.parameters?.['Layer'] as string) || element.category;
}

/** Project one BIM element into the shared protocol. */
export function bimElementToSceneObject(element: BIMElement): SceneObject {
    return {
        id: element.id,
        source: 'bim',
        kind: bimCategoryToKind(element.category),
        displayName: element.name || element.type || element.category,
        layerId: layerOf(element),
        visible: true, // per-element hidden state is resolved by VisibilityResolver
        locked: Boolean(element.parameters?.['Locked']),
        placement: extractPlacement(element),
        data: {
            category: element.category,
            familyType: element.type,
            parameters: element.parameters,
            geometryData: element.geometryData,
            phaseCreatedId: element.phaseCreatedId,
            phaseDemolishedId: element.phaseDemolishedId,
            designOptionId: element.designOptionId,
        },
    };
}

/**
 * Merge protocol-level edits made from another workbench (visibility,
 * lock, layer override) back into the BIM element's parameter bag so the
 * BIM store remains the single owner of the living element.
 */
export function applySceneObjectToBimElement(
    element: BIMElement,
    obj: SceneObject,
): BIMElement {
    return {
        ...element,
        parameters: {
            ...element.parameters,
            Locked: obj.locked,
            ...(obj.layerId !== element.category ? { Layer: obj.layerId } : {}),
        },
    };
}
